import React, { useState, useEffect } from "react";
import {
    Table,
    Button,
    Space,
    Image,
    Switch,
    message,
    Modal,
    Form,
    Input,
    Upload,
    Breadcrumb,
    Popconfirm,
    Select,
} from "antd";
import {
    PlusOutlined,
    UploadOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectCategories } from "../../store/slices/categoriesSlice";
import noImage from "../../assets/images/no-image.png";
import { useSubCategories } from '../../hooks/useSubCategories';
import { useCategories } from "../../hooks/useCategories";

const SubCategories = () => {
    const {
        createSubCategory,
        fetchSubCategories,
        deleteSubCategory,
        toggleSubCategoryStatus,
        updateSubCategory
    } = useSubCategories();
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { subCategories, loading } = useSelector((state) => state.subCategory);
    const { categories, fetchCategories } = useCategories();

    const [parentCategory, setParentCategory] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchSubCategories(categoryId);
    }, [dispatch]);

    useEffect(() => {
        fetchCategories();
    }, [categoryId]);

    useEffect(() => {
        const category = categories.find((s) => s.categoryId === categoryId || s._id === categoryId);
        if (category) {
            setParentCategory({
                id: category.id || category._id,
                name: category.name,
                image: category.image || noImage,
                isActive: category.isActive,
            });
        } else if (categoryId && categories.length > 0) {
            setParentCategory({
                id: categoryId,
                name: "Service",
                image: noImage,
                isActive: true,
            });
        }
    }, [categoryId, categories])

    // Helper function to strip uploads prefix from image path (for sending to backend)
    const stripUploadsPrefix = (imagePath) => {
        if (!imagePath || typeof imagePath !== 'string') return imagePath;
        if (imagePath.startsWith('/uploads/category/')) {
            return imagePath.replace('/uploads/category/', '');
        }
        if (imagePath.startsWith('/uploads/')) {
            return imagePath.replace('/uploads/', '');
        }
        return imagePath;
    };

    // Helper function to normalize image path: convert /uploads/ to /uploads/products/
    const normalizeImagePath = (imagePath) => {
        if (!imagePath || typeof imagePath !== 'string') return imagePath;
        if (imagePath.startsWith('/uploads/') && !imagePath.startsWith('/uploads/category/')) {
            return imagePath.replace('/uploads/', '/uploads/category/');
        }
        return imagePath;
    };

    // Helper function to get full image URL
    const getImageUrl = (image) => {
        if (!image) return noImage;
        if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:') || image.startsWith('blob:')) {
            return image;
        }
        const normalizedPath = normalizeImagePath(image);
        if (normalizedPath.startsWith('/uploads/category/')) {
            const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
            return `${baseURL}${normalizedPath}`;
        }
        const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
        const imagePath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
        return `${baseURL}/uploads/category${imagePath}`;
    };

    // Toggle Active/Inactive
    const handleToggle = async (id, checked) => {
        try {
            await toggleSubCategoryStatus(id, checked);
            await fetchSubCategories(categoryId); // Refetch to ensure UI is updated
            message.success(`Service ${checked ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            message.error('Failed to update service status');
        }
    };

    const handleAddSubService = () => {
        setEditingSubCategory(null);
        setIsSubmitting(false);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditSubService = (record) => {
        setEditingSubCategory(record);
        setIsSubmitting(false);
        setIsModalVisible(true);

        // Construct full image URL for display in edit form
        const imageUrl = record.image ? getImageUrl(record.image) : null;

        form.setFieldsValue({
            name: record.name,
            status: record.status === "1",
            image: imageUrl
                ? [
                    {
                        uid: "-1",
                        name: "no-img.png",
                        status: "done",
                        url: imageUrl, // full URL for display
                    },
                ]
                : [],
        });
    };

    const handleDeleteSubService = async (id) => {
        try {
            // await dispatch(deleteProduct(id)).unwrap();
            deleteSubCategory(id).unwrap();
            message.success("SubCategory deleted successfully");
        } catch {
            message.error("Failed to delete SubCategory");
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            let imageFile = null;

            if (values.image && values.image.length > 0) {
                const fileItem = values.image[0];
                if (fileItem.originFileObj) {
                    imageFile = fileItem.originFileObj; // new upload
                } else if (fileItem.url) {
                    // Extract filename from full URL or path
                    let imagePath = fileItem.url;
                    // If it's a full URL, extract the path part
                    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                        try {
                            const url = new URL(imagePath);
                            imagePath = url.pathname;
                        } catch (e) {
                            // If URL parsing fails, use as is
                        }
                    }
                    // Strip /uploads/ or /uploads/products/ prefix if present, keep only filename
                    imageFile = stripUploadsPrefix(imagePath);
                    // Remove leading slash if still present
                    if (imageFile.startsWith('/')) {
                        imageFile = imageFile.substring(1);
                    }
                }
            }

            const payload = {
                categoryId : categoryId,
                name: values.name,
                type: values.type,
                status: values.status ? "1" : "0",
                image: imageFile,
            };
            setIsSubmitting(true);
            if (editingSubCategory) {
                await updateSubCategory(editingSubCategory._id, payload);
                await fetchSubCategories(categoryId)
                // await dispatch(updateProduct({ id: editingSubCategory._id, data: payload })).unwrap();
                message.success("Sub Category updated successfully");
            } else {
                // await dispatch(createSubCategory(payload)).unwrap();
                await createSubCategory(payload)
                await fetchSubCategories(categoryId)
                setIsModalVisible(false);
                form.resetFields();
                message.success("Sub Category added successfully");
            }

            setIsModalVisible(false);
            setEditingSubCategory(null);
            setIsSubmitting(false);
            form.resetFields();
        } catch (err) {
            console.error(err);
            message.error("Failed to save sub category");
        }
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isImage) message.error("You can only upload images!");
        if (!isLt2M) message.error("Image must be smaller than 2MB!");
        return false;
    };

    const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

    const columns = [
        {
            title: "Image",
            dataIndex: "image",
            key: "image",
            width: 120,
            render: (image) => (
                <Image
                    src={getImageUrl(image)}
                    width={80}
                    height={80}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                />
            ),
        },
        { title: "Name", dataIndex: "name", key: "name" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <Switch
                        checked={status == "1"}
                        onChange={(checked) => handleToggle(record._id, checked)}
                        checkedChildren="Active"
                        unCheckedChildren="Inactive"
                    />
                </span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, record) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEditSubService(record)}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete this sub category?" onConfirm={() => handleDeleteSubService(record._id)}>
                        <Button icon={<DeleteOutlined />} size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (!parentCategory) return <div>Loading...</div>;

    return (
        <div>
            <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    { title: <Space onClick={() => navigate("/categories")}><HomeOutlined /> Categories</Space> },
                    { title: parentCategory.name },
                ]}
            />

            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/categories")}>
                    Back to Categories
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSubService}>
                    Add Sub Category
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={subCategories}
                rowKey="_id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} subcategories`,
                }}
                onRow={(record) => ({
                    onClick: () => navigate(`/categories/${record.categoryId}/sub-categories/${record?._id}/add-service`),
                    style: { cursor: 'pointer' },
                })}
            />

            <Modal
                title={editingSubCategory ? "Edit Sub Category" : "Add Sub Category"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
                okText={editingSubCategory ? "Update" : "Add"}
                confirmLoading={isSubmitting}
                okButtonProps={{ disabled: isSubmitting }}
            >
                <Form form={form} layout="vertical" initialValues={{ status: true }}>
                    <Form.Item label="Sub Category Name" name="name" rules={[{ required: true, message: "Please enter sub category name!" }]}>
                        <Input placeholder="Enter sub category name" />
                    </Form.Item>

                    {/* <Form.Item
                        label="Type"
                        name="type"
                        rules={[
                            { required: true, message: 'Please select type' },
                        ]}
                    >
                        <Select
                            options={[
                                { value: '', disabled : true, label: <span>Select Type</span> },
                                { value: 'simple', label: <span>Simple</span> },
                                { value: 'complex', label: <span>Complex</span> },
                            ]}
                        />
                    </Form.Item> */}

                    <Form.Item label="Sub Category Image" name="image" valuePropName="fileList" getValueFromEvent={normFile}>
                        <Upload listType="picture-card" beforeUpload={beforeUpload} maxCount={1}>
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item label="Status" name="status" valuePropName="checked">
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubCategories;
